import { useEffect, useState } from "react";

import {
  Badge,
  Checkbox,
  Modal,
  TextFieldInput,
} from "@smart/itops-sb-design-system-dom";

type PublishModalProps = {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  inFlightCount?: number;
  onConfirm: (args: {
    publishNote?: string;
    applyToInFlight: boolean;
  }) => void | Promise<void>;
};

export const PublishModal = ({
  open,
  onClose,
  loading,
  inFlightCount,
  onConfirm,
}: PublishModalProps) => {
  const [publishNote, setPublishNote] = useState("");
  const [applyToInFlight, setApplyToInFlight] = useState(false);

  const getInFlightMessage = () => {
    if (inFlightCount) {
      return `${inFlightCount} ${inFlightCount === 1 ? "person has" : "people have"} started filling out this form, they will see the changes on their next page reload.`;
    }
    return "Anyone who has started filling out this form will see the changes on their next page reload.";
  };

  useEffect(() => {
    if (!open) return;
    setPublishNote("");
    setApplyToInFlight(false);
  }, [open]);

  return (
    <Modal
      dataTestId="publish-form-modal"
      header={{ text: "Publish form" }}
      open={open}
      onClose={onClose}
      loading={loading}
      closeOptions={{ escapeKey: !loading, clickOutside: !loading }}
      footer={{
        buttons: [
          {
            text: "Cancel",
            type: "button",
            variant: "secondarySubtle",
            disabled: loading,
            onClick: onClose,
          },
          {
            text: "Publish",
            type: "button",
            variant: "primaryDefault",
            disabled: loading,
            onClick: () =>
              onConfirm({
                publishNote: publishNote.trim() || undefined,
                applyToInFlight,
              }),
          },
        ],
      }}
    >
      <div className="flex flex-col gap-medium text-paragraph">
        <p>Publishes a new version for new intake links and submissions.</p>
        <div className="flex items-center gap-small">
          <label htmlFor="publish-form-note" className="font-semibold">
            Change Note
          </label>
          <Badge theme="emphasis" label="Optional" />
        </div>
        <TextFieldInput
          id="publish-form-note"
          value={publishNote}
          onChange={(e) => setPublishNote(e.target.value)}
          disabled={loading}
        />
        <div className="flex flex-col gap-small">
          <Checkbox
            label="Also apply to forms in progress"
            checked={applyToInFlight}
            onChange={(checked) => setApplyToInFlight(checked)}
            disabled={loading || !inFlightCount}
          />
          <div className="flex flex-col gap-small ml-[2.8rem] text-caption text-neutral-500">
            {inFlightCount === 0 ? (
              <p>
                No one is filling in this form right now - nothing to update.
              </p>
            ) : (
              <>
                <p>{getInFlightMessage()}</p>
                <p>
                  <b>Note:</b> Their saved answers are kept where the field
                  still exists.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
